<template>
  <div id="accounts-receivable">
    <BreadCrumb :items="breadcrumbItems" />
    <v-card>
      <v-card-title class="header d-flex justify-space-between align-center">
        <h1 class="pa-0">Adicionais de contas a receber</h1>
        <div>
          <v-btn @click="handleExport" :loading="isExportButtonLoading" elevation="2" color="info" class="mr-2" large>
            Exportar planilha</v-btn
          >
          <v-btn elevation="2" color="success" @click="openAditionalForm" large> Novo adicional </v-btn>
        </div>
      </v-card-title>

      <v-card-text>
        <v-expansion-panels accordion class="mt-2 mb-6">
          <v-expansion-panel class="elevation-1">
            <v-expansion-panel-header color="#f2f2f2" class="px-4">
              <span>Filtrar por</span>
              <template v-slot:actions>
                <v-icon color="black"> mdi-chevron-down </v-icon>
              </template>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row class="mt-2 pb-1">
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="isInitialDateVisible"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedInitialDate"
                        label="Data inicial"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="initialDate" locale="pt-br"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="isEndDateVisible"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="formattedEndDate"
                        label="Data final"
                        prepend-icon="mdi-calendar"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker v-model="endDate" locale="pt-br"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="isDiscount"
                    :items="isDiscountItems"
                    item-value="value"
                    label="Tipo de adicional"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select v-model="linkedInvoice" :items="linkedInvoiceItems" item-value="value" label="Status" />
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="senderId"
                    :items="senders"
                    item-text="name"
                    item-value="id"
                    label="Nome do Embarcador"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-combobox
                    v-model="numberCte"
                    :search-input.sync="cteNumberInput"
                    append-icon=""
                    label="Número da CTE"
                    hide-selected
                    multiple
                    chips
                    deletable-chips
                    @keydown.native="filterInpuT"
                    @change="saveCteNumber"
                  ></v-combobox>
                </v-col>

                <v-col cols="12" class="d-flex justify-end align-center">
                  <v-btn
                    elevation="2"
                    :disabled="!hasFilters"
                    :dark="hasFilters"
                    color="red"
                    class="mx-2"
                    @click="clearFilters()"
                  >
                    Limpar filtros
                  </v-btn>

                  <v-btn
                    :loading="isLoading"
                    @click="search()"
                    :disabled="!hasFilters"
                    :dark="hasFilters"
                    elevation="2"
                    color="green"
                  >
                    Buscar
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-data-table
        class="px-5"
        :headers="aditionalsHeaders"
        :items="aditionals"
        :items-per-page="pageSize"
        @update:items-per-page="changeItemsPerPage"
        :loading="isLoading"
        :footer-props="{
          'items-per-page-options': [10, 20, 30, 50],
          itemsPerPageText: 'Itens por página'
        }"
        no-data-text="Nenhum resultado encontrado"
      >
        <template v-slot:[`item.isDiscount`]="{ item }">
          <span> {{ getAditionalType(item.isDiscount) }}</span>
        </template>
        <template v-slot:[`item.price`]="{ item }">
          <span> {{ getFormattedPrice(item.price) }}</span>
        </template>
        <template v-slot:[`item.additionalDate`]="{ item }">
          <span> {{ getFormattedDate(item.additionalDate) }}</span>
        </template>
        <template v-slot:[`item.numberCte`]="{ item }">
          <span>
            <TruncateDataWithTooltip
              :data="item.numberCte"
              :lengthDataToTruncate="lengthDataToTruncate"
            ></TruncateDataWithTooltip>

            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small @click="copy(item.accessKeyCte)" v-bind="attrs" v-on="on">
                  mdi-checkbox-multiple-blank-outline
                </v-icon>
              </template>

              <span class="d-block">Clique para copiar chave de acesso da CTE:</span>
              <span>{{ item.accessKeyCte }}</span>
            </v-tooltip>
          </span>
        </template>

        <template v-slot:[`item.observation`]="{ item }">
          <copy-to-clipboard-tooltip v-if="item.observation != null && item.observation" :text="item.observation" />

          <span v-else>-</span>
        </template>
        <template v-slot:[`item.invoiceId`]="{ item }">
          <span>
            <v-chip :color="getChipColor(item.invoiceId)" dark>
              {{ getChipLabel(item.invoiceId) }}
            </v-chip>
          </span>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <div class="d-flex justify-center">
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-icon color="black" dense v-bind="attrs" v-on="on"> mdi-menu </v-icon>
              </template>
              <v-list>
                <v-list-item @click="handleAditionalToEdit(item)">
                  <v-list-item-title>{{ getViewOrEditLabel(item) }}</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="!hasInvoiceReceivable(item.invoiceId)" @click="handleAditionalToDelete(item)">
                  <v-list-item-title style="color: red">Deletar</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </template>
      </v-data-table>
      <v-pagination v-model="pageNumber" :length="totalPages" :total-visible="7"></v-pagination>
    </v-card>
    <aditional-form-dialog :isOpen.sync="isAditionalFormOpen" @shouldUpdateList="getAditionals" />
    <aditional-edit-form
      :isOpen.sync="isEditionDialogOpen"
      :aditionalToEdit="aditionalToEdit"
      @shouldUpdateList="getAditionals"
    />
    <delete-confirm-dialog
      :isOpen.sync="isDeleteDialogOpen"
      @confirmClick="handleDeleteAditional"
      :isConfirmBtnLoading="isDeleteButtonLoading"
      title="Deletar Adicional"
      body-text="Você confirma deletar este adicional?"
    />
    <MessagesError :errors="errors" :type="errorsType" :error500="error500" @closeMessages="errors = []" />
  </div>
</template>

<script>
import BreadCrumb from '../../../components/BreadCrumb.vue';
import { mapActions } from 'vuex';
import AditionalFormDialog from '@/components/AccountsReceivable/AditionalFormDialog';
import AditionalEditForm from '@/components/AccountsReceivable/AditionalEditForm';
import DeleteConfirmDialog from '@/components/ConfirmDialog';
import { formatDate } from '@/handlers/DateHandler';
import TruncateDataWithTooltip from '@/components/Table/TruncateDataWithTooltip.vue';
import CopyToClipboardTooltip from '@/components/CopyToClipboardTooltip.vue';
import copyToClipboard from '@/utils/copyToClipboard';
import MessagesError from '@/components/MessagesError.vue';
import comboboxFormatter from '@/utils/comboboxFormatter';
import allowedChars from '@/utils/allowedChars';

export default {
  name: 'AditionalsAccountsReceivable',
  components: {
    BreadCrumb,
    AditionalFormDialog,
    AditionalEditForm,
    DeleteConfirmDialog,
    CopyToClipboardTooltip,
    TruncateDataWithTooltip,
    MessagesError
  },
  props: {
    lengthDataToTruncate: {
      default: () => 10,
      type: Number
    }
  },
  data: () => ({
    nodes: [],
    pageNumber: 1,
    pageSize: 10,
    totalPages: 10,
    totalRecords: null,
    isLoading: false,
    isExportButtonLoading: false,
    isDeleteDialogOpen: false,
    isDeleteButtonLoading: false,
    aditionalsHeaders: [
      {
        value: 'nameSender',
        text: 'NOME DO EMBARCADOR',
        sortable: false
      },
      {
        value: 'isDiscount',
        text: 'TIPO',
        sortable: false
      },
      {
        value: 'price',
        text: 'VALOR',
        sortable: false
      },
      {
        value: 'additionalDate',
        text: 'DATA DE CRIAÇÃO',
        sortable: false
      },
      {
        value: 'numberCte',
        text: 'NÚMERO CTE',
        sortable: false
      },
      {
        value: 'justification',
        text: 'JUSTIFICATIVA',
        sortable: false
      },
      {
        value: 'observation',
        text: 'OBSERVAÇÃO',
        sortable: false
      },

      {
        value: 'invoiceId',
        text: 'STATUS',
        align: 'center',
        sortable: false
      },
      {
        value: 'actions',
        text: 'AÇÕES',
        sortable: false
      }
    ],
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Financeiro',
        disabled: true,
        to: '/couriers-accounts-payable/'
      },
      {
        text: 'Contas a receber',
        disabled: true
      },
      {
        text: 'Adicionais',
        disabled: true
      }
    ],
    aditionals: [],
    senders: [],
    aditionalToEdit: {
      id: null,
      senderId: null,
      isDiscount: null,
      price: null,
      justificationId: null,
      additionalDate: null,
      accessKeyCte: null,
      observation: null,
      xmlFileName: null
    },
    aditionalToDelete: null,
    senderId: null,
    numberCte: [],
    cteNumberInput: '',
    isDiscountItems: [
      { text: 'Crédito', value: false },
      { text: 'Débito', value: true }
    ],
    linkedInvoice: null,
    initialDate: null,
    endDate: null,
    isDiscount: null,
    isAditionalFormOpen: false,
    isEditionDialogOpen: false,
    linkedInvoiceItems: [
      { text: 'Vinculado', value: true },
      { text: 'Não vinculado', value: false }
    ],
    isInitialDateVisible: false,
    isEndDateVisible: false,
    errors: [],
    errorsType: 'error',
    error500: null
  }),

  computed: {
    formattedInitialDate() {
      const date = this.initialDate;
      return date ? formatDate(date) : null;
    },
    formattedEndDate() {
      const date = this.endDate;
      return date ? formatDate(date) : null;
    },
    hasFilters() {
      if (
        this.initialDate ||
        this.endDate ||
        this.isDiscount !== null ||
        this.linkedInvoice !== null ||
        this.senderId ||
        this.numberCte.length !== 0
      ) {
        return true;
      }
      return false;
    },

    getTheLastThirtyDays() {
      const now = new Date();
      const currentDay = now;
      const thirtyDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);

      return {
        thirtyDayAgo: thirtyDayAgo.toISOString().slice(0, 10),
        currentDay: currentDay.toISOString().slice(0, 10)
      };
    }
  },

  watch: {
    pageNumber: {
      handler() {
        this.getAditionals();
      }
    }
  },

  async created() {
    this.filterTheLastThirtyDays();
    await this.getAditionals();
    await this.getAllSenders();
  },

  methods: {
    ...mapActions('AccountsReceivableStore', [
      'fetchAditionalsReceivable',
      'deleteAditional',
      'exportAditionalSheet',
      'getSenders'
    ]),

    async getAditionals() {
      try {
        this.isLoading = true;
        const filters = this.getFilters();
        const { data, totalPages, totalRecords } = await this.fetchAditionalsReceivable({
          ...filters,
          pageSize: this.pageSize,
          pageNumber: this.pageNumber
        });

        this.aditionals = data;
        this.totalPages = totalPages;
        this.totalRecords = totalRecords;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    filterTheLastThirtyDays() {
      const { thirtyDayAgo, currentDay } = this.getTheLastThirtyDays;
      this.initialDate = thirtyDayAgo;
      this.endDate = currentDay;
    },

    async handleDeleteAditional() {
      try {
        const { id } = this.aditionalToDelete;
        this.isDeleteButtonLoading = true;
        const data = await this.deleteAditional(id);
        if (data.success) {
          this.handleDeleteSuccessFeedback();
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
          this.isDeleteDialogOpen = false;
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isDeleteButtonLoading = false;
      }
    },

    async handleExport() {
      try {
        this.isExportButtonLoading = true;
        await this.getAditionals();
        const filters = this.getFilters();
        const data = await this.exportAditionalSheet({ ...filters, pageSize: this.totalRecords });
        if (data.success) {
          this.errors = ['Planilha exportada com sucesso!'];
          this.errorsType = 'success';
          setTimeout(() => {
            this.errors = [];
          }, 3000);
        } else {
          this.errors = data.errors;
          this.errorsType = 'error';
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.isExportButtonLoading = false;
      }
    },

    async getAllSenders() {
      try {
        this.senders = await this.getSenders();
      } catch (error) {
        console.error(error);
      }
    },

    handleAditionalToEdit(aditional) {
      this.isEditionDialogOpen = true;
      this.aditionalToEdit = aditional;
    },

    handleAditionalToDelete(aditional) {
      this.isDeleteDialogOpen = true;
      this.aditionalToDelete = aditional;
    },

    handleDeleteSuccessFeedback() {
      this.errors = ['Adicional deletado com sucesso!'];
      this.errorsType = 'success';
      this.isDeleteDialogOpen = false;
      this.getAditionals();
      setTimeout(() => {
        this.errors = [];
      }, 3000);
    },

    getFilters() {
      return {
        pageNumber: this.pageNumber,
        pageSize: this.pageSize,
        initialDate: this.initialDate,
        endDate: this.endDate,
        senderId: this.senderId,
        isDiscount: this.isDiscount,
        numberCte: this.numberCte,
        linkedInvoice: this.linkedInvoice
      };
    },

    filterInput(event) {
      return allowedChars(event, /[0-9,.;/]/);
    },

    saveCteNumber(value) {
      this.numberCte = comboboxFormatter(value);
    },

    clearFilters() {
      this.initialDate = null;
      this.endDate = null;
      this.linkedInvoice = null;
      this.isDiscount = null;
      this.senderId = null;
      this.numberCte = [];

      this.getAditionals();
    },

    search() {
      this.pageNumber = 1;
      this.getAditionals();
    },

    copy(str) {
      copyToClipboard(str);
    },

    getAditionalType(isDiscount) {
      return isDiscount ? 'Débito' : 'Crédito';
    },

    getFormattedPrice(price) {
      return price.toLocaleString('pt-BR', { minimumFractionDigits: 2, style: 'currency', currency: 'BRL' });
    },

    getFormattedDate(date) {
      return formatDate(date);
    },

    getChipColor(invoiceId) {
      return this.hasInvoiceReceivable(invoiceId) ? 'green' : 'red';
    },

    getChipLabel(invoiceId) {
      return this.hasInvoiceReceivable(invoiceId) ? 'Vinculado' : 'Não vinculado';
    },

    getViewOrEditLabel(item) {
      return this.hasInvoiceReceivable(item.invoiceId) ? 'Visualizar' : 'Editar';
    },

    changeItemsPerPage(items) {
      this.pageSize = items;
      this.getAditionals();
    },

    openAditionalForm() {
      this.isAditionalFormOpen = true;
    },

    hasInvoiceReceivable(invoiceId) {
      return invoiceId !== '00000000-0000-0000-0000-000000000000' && invoiceId !== null;
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep.v-select.v-select--chips .v-select__selections {
  min-height: 50px;
}
</style>
